import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate }   from 'react-router-dom';
import { getTables }     from '../api/tables.api.js';
import { useTabsStore }  from '../store/tabsStore.js';
import { esComandera, esCompartida } from '../store/deviceModeStore.js';
import { useWaiterStore } from '../store/waiterStore.js';
import WaiterPinSheet from '../components/WaiterPinSheet.jsx';
import CuantosSonSheet from '../components/CuantosSonSheet.jsx';
import { useConfirmacion } from '../components/ConfirmSheet.jsx';
import MenuSheet from '../components/MenuSheet.jsx';
import { cerrarSesion } from '../lib/salir.js';
import { useCartStore }  from '../store/cartStore.js';
import { onHubEvent, usingHub } from '../lib/hub.js';
import { tabTotal }      from '../lib/tableSync.js';
import { IcoTables, IcoUser, IcoAparato } from '../components/common/Icons.jsx';
import { useEsTelefono } from '../lib/viewport.js';
import styles from './TableMapScreen.module.css';

// El plano se dibuja sobre una retícula de ancho fijo: así el salón se ve igual
// en la tablet del mesero y en el monitor de la caja, y el editor del dashboard
// habla en las mismas unidades.
const GRID_COLS = 12;
const POLL_MS   = 10000;

const fmt = (n) => `$${Number(n ?? 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

// Cuánto lleva sentada la mesa. Ayuda a ver de un vistazo cuál lleva mucho sin
// cobrar, que es justo lo que un encargado busca en el plano.
const elapsed = (iso) => {
  if (!iso) return null;
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1)  return 'recién';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ${min % 60} min`;
  // Una mesa "de 489 h" es una cuenta que nadie cerró: en días se entiende.
  return `${Math.floor(h / 24)} d`;
};

export default function TableMapScreen() {
  const navigate = useNavigate();

  const tabs        = useTabsStore((s) => s.tabs);
  const [cuantosPara, setCuantosPara] = useState(null);   // mesa que se está sentando
  const createTab   = useTabsStore((s) => s.createTab);
  const openInCart  = useTabsStore((s) => s.openInCart);
  const saveActive  = useTabsStore((s) => s.saveActive);
  const bindTable   = useTabsStore((s) => s.bindTable);
  const loadTabs    = useTabsStore((s) => s.load);
  const activeTabId = useTabsStore((s) => s.activeTabId);

  const cartItems   = useCartStore((s) => s.items);

  const [tables,  setTables]  = useState([]);
  const [zone,    setZone]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [msg,     setMsg]     = useState(null);
  // Mesa que se tocó sin estar identificado: se retoma en cuanto entre el PIN.
  const [pinPara, setPinPara] = useState(null);
  const [pinParaMesa, setPinParaMesa] = useState(null);   // qué mesa se está tomando
  const waiter = useWaiterStore((s) => s.waiter);
  const [busyId,  setBusyId]  = useState(null);
  const { pedir, Confirmacion } = useConfirmacion();
  const telefono = useEsTelefono();
  const [menuOpen, setMenuOpen] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await getTables();
      setTables(res.data ?? []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error ?? 'No se pudo cargar el plano');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); loadTabs(); }, [load, loadTabs]);

  // Llegar al plano significa que se terminó con la mesa anterior. En una
  // tablet compartida ese es el momento de soltar la identidad: el siguiente
  // que la tome se identifica con su PIN. Sin esto, el primero que teclea en
  // la mañana queda como el mesero de todo el día y se lleva las propinas.
  useEffect(() => { useWaiterStore.getState().soltarSiCompartida(); }, []);

  // Con caja central el plano llega por WebSocket cuando alguien sienta o cobra
  // una mesa. Sin caja, se sondea: un plano viejo es peor que ninguno.
  useEffect(() => {
    const off = onHubEvent('tables', (list) => { setTables(list ?? []); setLoading(false); });
    const id  = setInterval(() => { load(true); if (!usingHub()) loadTabs(); }, usingHub() ? 60000 : POLL_MS);
    return () => { off(); clearInterval(id); };
  }, [load, loadTabs]);

  const zones = useMemo(
    () => [...new Set(tables.map((t) => t.zone))].sort((a, b) => a.localeCompare(b, 'es')),
    [tables]
  );

  /**
   * En qué zona abre el plano.
   *
   * Arrancaba en la primera por orden alfabético, que en casi cualquier
   * restaurante es "Barra" —donde nadie levanta comandas—. Eso costaba un toque
   * en cada viaje al plano, decenas por turno. Ahora abre donde está el salón:
   * la zona con más mesas, y a igualdad la primera. El mesero puede cambiarse y
   * su elección manda.
   */
  const zonaPorDefecto = useMemo(() => {
    if (!zones.length) return null;
    return zones.reduce((mejor, z) =>
      (tables.filter((t) => t.zone === z).length > tables.filter((t) => t.zone === mejor).length ? z : mejor));
  }, [zones, tables]);

  const activeZone = zone ?? zonaPorDefecto;
  const visibles   = tables.filter((t) => t.zone === activeZone);

  const filas = visibles.reduce((max, t) => Math.max(max, (t.y ?? 0) + (t.h ?? 2)), 0);


  const flash = (text) => { setMsg(text); setTimeout(() => setMsg(null), 3500); };

  // ─── Estado de cada mesa ────────────────────────────────────────────────────
  // Las cuentas son compartidas: cualquier tablet puede abrir cualquier mesa.
  // La distinción "la abrí yo" vs "la abrió otro" ya solo es informativa: se
  // pinta distinto para que el mesero ubique las suyas de un vistazo.
  const estadoDe = (table) => {
    const tab = tabs.find((t) => t.tableId === table._id);
    if (tab) return { kind: tab.id === activeTabId ? 'activa' : 'ocupada', tab };
    if (table.occupancy) return { kind: 'ocupada', occ: table.occupancy };
    return { kind: 'libre' };
  };

  // En teléfono el croquis de doce columnas no cabe (756 px en 360) y el
  // mesero busca "Mesa 4", no una posición: se lista en cuadrícula, las mías
  // primero y después en el orden del salón (arriba a abajo, izquierda a
  // derecha), que es el que ya conoce.
  const rango = (t) => { const k = estadoDe(t).kind; return k === 'activa' ? 0 : k === 'ocupada' ? 1 : 2; };
  const enLista = telefono
    ? [...visibles].sort((a, b) => rango(a) - rango(b) || (a.y ?? 0) - (b.y ?? 0) || (a.x ?? 0) - (b.x ?? 0))
    : visibles;

  // En tablet compartida no se opera una mesa sin decir quién eres. Se guarda
  // qué se iba a hacer para retomarlo al entrar el PIN, en vez de obligar a
  // tocar la mesa otra vez.
  const conIdentidad = (accion, mesa = null) => {
    if (esCompartida() && !useWaiterStore.getState().waiter) {
      setPinPara(() => accion);
      setPinParaMesa(mesa);
      return true;
    }
    return false;
  };

  const abrirCuenta = async (tab) => {
    if (conIdentidad(() => abrirCuenta(tab), tab.tableName ?? tab.name)) return;
    if (cartItems.length > 0 && tab.id !== activeTabId) {
      if (!(await pedir({ titulo: `¿Abrir ${tab.tableName ?? tab.name}?`, texto: 'Lo que hay en el carrito se reemplaza con esta cuenta.', confirmar: 'Abrir' }))) return;
    }
    openInCart(tab);
    navigate('/');
  };

  /**
   * Sentar una mesa empieza por preguntar cuántos son.
   *
   * El plano ya sabe cuántos lugares tiene cada mesa y hasta ahora ese dato no
   * se usaba para nada: el mesero llegaba al carrito y daba de alta comensales
   * de uno en uno, mientras pedían. Con esto la mesa arranca con su gente
   * sentada y el mesero solo confirma un número que ya viene puesto.
   */
  const sentar = async (table) => {
    if (conIdentidad(() => sentar(table), table.name)) return;
    if (cartItems.length > 0) {
      if (!(await pedir({ titulo: `¿Sentar en ${table.name}?`, texto: 'Lo que hay en el carrito se guarda como la cuenta de esta mesa.', confirmar: 'Sentar' }))) return;
    }
    setCuantosPara(table);
  };

  const abrirMesa = async (table, cuantos) => {
    setCuantosPara(null);
    setBusyId(table._id);
    try {
      const cart = useCartStore.getState();
      // Se sientan ANTES de crear la cuenta para que viajen en ella desde el
      // primer guardado: si se hiciera después, una tablet que leyera la mesa
      // en ese hueco la vería vacía de gente.
      if (cuantos > 0) cart.sentarComensales(cuantos);
      // Si el carrito ya es una cuenta (de barra, por ejemplo), se le amarra la
      // mesa en vez de abrir otra.
      const tab = activeTabId
        ? await bindTable(activeTabId, table._id, table.name)
        : await createTab(table.name, {
            items:    cart.items,
            customer: cart.customer,
            comensales: cart.comensales,
            discount: cart.discount,
            note:     cart.note,
            tableId:  table._id,
            tableName: table.name,
          });
      openInCart(tab);
      // `openInCart` repone el carrito desde la cuenta del servidor. Si la
      // cuenta YA existía —una de barra que se amarra a la mesa— el guardado
      // que la amarró no llevó a la gente, así que se sienta otra vez sobre la
      // cuenta ya abierta y se guarda. Sin esto, sentar cuatro y amarrar la
      // mesa dejaba la mesa sin nadie.
      if (cuantos > 0 && !(tab.comensales ?? []).length) {
        useCartStore.getState().sentarComensales(cuantos);
        saveActive().catch(() => {});
      }
      navigate('/');
    } catch (err) {
      const conflict = err.response?.status === 409;
      flash(conflict
        ? (err.response.data?.error ?? `${table.name} ya la tomó otro mesero`)
        : (err.response?.data?.error ?? 'No se pudo abrir la mesa en la caja'));
      loadTabs();
      load(true);
    } finally {
      setBusyId(null);
    }
  };

  // Cuenta TODAS las mesas, no solo las de la zona abierta: decía "0 de 3
  // ocupadas" con media casa llena, porque el salón estaba en otra pestaña.
  const ocupadas = tables.filter((t) => estadoDe(t).kind !== 'libre').length;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        {/* En comandera sin cuenta abierta, el plano ES el inicio: "Volver"
            llevaría a la raíz, que rebota aquí mismo. Un botón que no hace
            nada es peor que no tenerlo. */}
        {!(esComandera() && !activeTabId) && (
          <button className="btn btn--ghost" onClick={() => navigate('/')}>← Volver</button>
        )}
        <h1 className={styles.title}>Mesas</h1>
        <span className={styles.count}>
          {ocupadas} de {tables.length} ocupada{ocupadas !== 1 ? 's' : ''}
        </span>
        {/* Siempre hay salida. En comandera el plano es el inicio y no tenía
            ni menú ni "Volver": un mesero (o quien instala) no podía ver las
            cuentas, cambiar el aparato ni cerrar sesión. */}
        <button className={`btn btn--ghost ${styles.menuBtn}`} onClick={() => setMenuOpen(true)} aria-label="Más opciones">⋯</button>
      </div>

      <MenuSheet
        open={menuOpen}
        title="Mesas"
        onClose={() => setMenuOpen(false)}
        items={[
          { label: 'Cuentas abiertas', hint: 'Mesas y cuentas por nombre', icon: <IcoTables size={18} />, onClick: () => navigate('/tabs') },
          { label: 'Este aparato', hint: 'Caja o comandera, y la conexión con la caja principal', icon: <IcoAparato size={18} />, onClick: () => navigate('/hub') },
          { label: 'Cerrar sesión', danger: true, icon: <IcoUser size={18} />, onClick: async () => {
            setMenuOpen(false);
            if (await pedir({ titulo: '¿Cerrar sesión en este aparato?', texto: 'Las cuentas abiertas siguen guardadas. Para volver a entrar se pide el correo y la contraseña.', confirmar: 'Cerrar sesión', peligro: true })) {
              cerrarSesion();
              navigate('/login', { replace: true });
            }
          } },
        ]}
      />

      {zones.length > 1 && (
        <div className={styles.zones}>
          {zones.map((z) => (
            <button
              key={z}
              className={`${styles.zoneBtn} ${z === activeZone ? styles.zoneBtnActive : ''}`}
              onClick={() => setZone(z)}
            >{z}</button>
          ))}
        </div>
      )}

      {msg   && <p className={styles.msg}>{msg}</p>}
      {error && <p className={styles.error}>{error}</p>}

      {loading ? (
        <p className={styles.hint}>Cargando plano…</p>
      ) : tables.length === 0 && error ? (
        // Que no cargaran no es lo mismo que no haber mesas: decía "Todavía no
        // hay plano" cuando la caja o el internet no contestaban.
        <div className={styles.empty}>
          <p className={styles.emptyIcon}><IcoTables size={40} /></p>
          <p className={styles.emptyTitle}>No se pudieron cargar las mesas</p>
          <p className={styles.emptyHint}>Revisa el internet o que la caja principal esté encendida.</p>
          <button className="btn btn--primary" style={{ marginTop: 16 }} onClick={() => load()}>
            Reintentar
          </button>
        </div>
      ) : tables.length === 0 ? (
        <div className={styles.empty}>
          <p className={styles.emptyIcon}><IcoTables size={40} /></p>
          <p className={styles.emptyTitle}>Todavía no hay mesas</p>
          <p className={styles.emptyHint}>
            Se dan de alta en el panel web, en Catálogo → Mesas. Mientras tanto se pueden
            guardar cuentas por nombre.
          </p>
          <button className="btn btn--primary" style={{ marginTop: 16 }} onClick={() => navigate('/tabs')}>
            Ver cuentas abiertas
          </button>
        </div>
      ) : (
        <div
          className={telefono ? styles.cuadricula : styles.plano}
          style={telefono ? undefined : {
            gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)`,
            gridTemplateRows:    `repeat(${Math.max(filas, 1)}, minmax(76px, auto))`,
          }}
        >
          {enLista.map((table) => {
            const st    = estadoDe(table);
            const busy  = busyId === table._id;
            const redonda = table.shape === 'circle';
            const kindClass = st.kind === 'libre' ? 'k_libre' : st.kind === 'activa' ? 'k_mia' : 'k_ajena';

            return (
              <div
                key={table._id}
                className={[
                  styles.mesa,
                  styles[kindClass],
                  redonda ? styles.redonda : '',
                  busy ? styles.busy : '',
                ].filter(Boolean).join(' ')}
                style={telefono ? undefined : {
                  gridColumn: `${Math.floor(table.x ?? 0) + 1} / span ${Math.max(1, Math.round(table.w ?? 2))}`,
                  gridRow:    `${Math.floor(table.y ?? 0) + 1} / span ${Math.max(1, Math.round(table.h ?? 2))}`,
                }}
              >
                <button
                  className={styles.mesaBtn}
                  disabled={busy}
                  onClick={() => {
                    if (st.kind === 'libre') return sentar(table);
                    if (st.tab) return abrirCuenta(st.tab);
                    // Ocupada según el servidor pero la cuenta aún no llegó a esta tablet
                    flash(`${table.name} la trae ${st.occ?.waiter_name ?? 'otro mesero'}. Recargando cuentas…`);
                    loadTabs();
                  }}
                >
                  <span className={styles.mesaNombre}>{table.name}</span>

                  {st.kind === 'libre' && (
                    <span className={styles.mesaLibre}>
                      {table.seats} lugar{table.seats !== 1 ? 'es' : ''}
                    </span>
                  )}

                  {st.tab && (
                    <>
                      <span className={styles.mesaTotal}>{fmt(tabTotal(st.tab))}</span>
                      {st.tab.openedByName && (
                        <span className={styles.mesaMeta}><IcoUser size={11} /> {st.tab.openedByName}</span>
                      )}
                      <span className={styles.mesaMeta}>{elapsed(st.tab.createdAt)}</span>
                    </>
                  )}

                  {!st.tab && st.occ && (
                    <>
                      <span className={styles.mesaTotal}>{fmt(st.occ.total)}</span>
                      <span className={styles.mesaMeta}>
                        <IcoUser size={11} /> {st.occ.waiter_name ?? 'otra tablet'}
                      </span>
                      <span className={styles.mesaMeta}>{elapsed(st.occ.opened_at)}</span>
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    <WaiterPinSheet
        open={!!pinPara}
        para={pinParaMesa}
        onClose={() => { setPinPara(null); setPinParaMesa(null); }}
        onDone={() => { const seguir = pinPara; setPinPara(null); setPinParaMesa(null); seguir?.(); }}
      />
      <CuantosSonSheet
        open={!!cuantosPara}
        table={cuantosPara}
        onClose={() => setCuantosPara(null)}
        onConfirmar={(n) => abrirMesa(cuantosPara, n)}
      />
      {Confirmacion}
    </div>
  );
}
